import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersAPI, rolesAPI, branchesAPI, organizationsAPI, employeesAPI, QUERY_KEYS } from '@/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Badge, Modal } from '@/components/ui';
import { LoadingSpinner, Pagination } from '@/components/common';
import { User, Plus, Edit2, Trash2, Key, Shield } from 'lucide-react';
import { toast } from 'react-toastify';
import { extractPaginatedData } from '@/utils/pagination';
import { useDebouncedValue } from '@/utils/useDebouncedValue';

export default function UsersPage() {
  const perPage = 10;
  const { can, hasRole } = useAuth();
  const canManageUsers = can('manage_users');
  const canSelectOrganization = hasRole('super_admin');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [showPasswordModal, setShowPasswordModal] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());
  const queryClient = useQueryClient();

  const { data: usersData, isLoading } = useQuery({
    queryKey: [...QUERY_KEYS.users(), debouncedSearch, page],
    queryFn: () => usersAPI.list({ page, search: debouncedSearch }),
  });

  const { data: rolesData } = useQuery({
    queryKey: QUERY_KEYS.roles(),
    queryFn: () => rolesAPI.list(),
  });

  const { data: branchesData } = useQuery({
    queryKey: QUERY_KEYS.branches(),
    queryFn: () => branchesAPI.list(),
  });

  const { data: organizationsData } = useQuery({
    queryKey: QUERY_KEYS.organizations(),
    queryFn: () => organizationsAPI.list(),
    enabled: canSelectOrganization,
  });

  // Employees without linked user account (for linking)
  const { data: employeesData } = useQuery({
    queryKey: [...QUERY_KEYS.employees(), 'unlinked'],
    queryFn: () => employeesAPI.list({ per_page: 500 }),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => usersAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employees() });
      setShowForm(false);
      toast.success('Foydalanuvchi muvaffaqiyatli yaratildi');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => usersAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employees() });
      setEditingUser(null);
      toast.success('Foydalanuvchi muvaffaqiyatli yangilandi');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => usersAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users() });
      toast.success("Foydalanuvchi o'chirildi");
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => usersAPI.changePassword(id, data),
    onSuccess: () => {
      setShowPasswordModal(null);
      toast.success('Parol muvaffaqiyatli o\'zgartirildi');
    },
  });

  const { items: users, meta } = extractPaginatedData<any>(usersData, page, perPage);
  const roles = Array.isArray(rolesData?.data)
    ? rolesData.data
    : rolesData?.data?.data || [];
  const branches = Array.isArray(branchesData?.data)
    ? branchesData.data
    : branchesData?.data?.data || [];
  const organizations = Array.isArray(organizationsData?.data)
    ? organizationsData.data
    : organizationsData?.data?.data || [];
  
  // Get employees that don't have a user account yet (user_id is null)
  const allEmployees = Array.isArray(employeesData?.data)
    ? employeesData.data
    : employeesData?.data?.data || [];
  const availableEmployees = allEmployees.filter((emp: any) => !emp.user_id);

  const handleDelete = async (id: number) => {
    if (window.confirm("Foydalanuvchini o'chirishni xohlaysizmi?")) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 page-shell">
      <div className="page-toolbar">
        <h1 className="text-3xl font-bold">Foydalanuvchilar</h1>
        {canManageUsers && (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Yangi foydalanuvchi
          </Button>
        )}
      </div>

      {/* Search */}
      <Input
        type="search"
        name="user_search"
        placeholder="Ism yoki email bo'yicha qidirish..."
        value={search}
        onChange={(e) => {
          setPage(1);
          setSearch(e.target.value);
        }}
        autoComplete="off"
        data-lpignore="true"
      />

      <Modal
        isOpen={showForm || !!editingUser}
        onClose={() => {
          setShowForm(false);
          setEditingUser(null);
        }}
        title={editingUser ? 'Foydalanuvchini tahrirlash' : 'Yangi foydalanuvchi'}
      >
        <UserForm
          initialData={editingUser}
          roles={roles}
          branches={branches}
          organizations={organizations}
          employees={availableEmployees}
          canSelectOrganization={canSelectOrganization}
          isEdit={!!editingUser}
          onSubmit={async (data) => {
            if (editingUser) {
              await updateMutation.mutateAsync({ id: editingUser.id, data });
            } else {
              await createMutation.mutateAsync(data);
            }
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingUser(null);
          }}
        />
      </Modal>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <Modal
          isOpen={!!showPasswordModal}
          onClose={() => setShowPasswordModal(null)}
          title="Parolni o'zgartirish"
        >
          <PasswordForm
            onSubmit={async (data) => {
              await changePasswordMutation.mutateAsync({ id: showPasswordModal, data });
            }}
            onCancel={() => setShowPasswordModal(null)}
          />
        </Modal>
      )}

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto ui-table-wrap">
            <table className="w-full ui-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">Foydalanuvchi</th>
                  <th className="text-left py-3 px-4 font-semibold">Email</th>
                  <th className="text-left py-3 px-4 font-semibold">Rol</th>
                  <th className="text-left py-3 px-4 font-semibold">Tashkilot</th>
                  <th className="text-left py-3 px-4 font-semibold">Filial</th>
                  <th className="text-left py-3 px-4 font-semibold">Xodim</th>
                  <th className="text-left py-3 px-4 font-semibold">Holat</th>
                  <th className="text-left py-3 px-4 font-semibold">Amallar</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user: any) => (
                  <tr key={user.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <span className="font-medium">{user.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">{user.email}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <Shield className="w-4 h-4 text-purple-600" />
                        {user.role || user.roles?.[0]?.name || '-'}
                      </div>
                    </td>
                    <td className="py-3 px-4">{user.organization?.name || '-'}</td>
                    <td className="py-3 px-4">{user.branch?.name || 'All'}</td>
                    <td className="py-3 px-4">
                      {user.employee ? (
                        <span className="text-sm text-green-700">
                          {user.employee.full_name} ({user.employee.employee_id})
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant={user.is_active ? 'success' : 'secondary'}>
                        {user.is_active ? 'Faol' : 'Nofaol'}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      {canManageUsers && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingUser(user)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowPasswordModal(user.id)}
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(user.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <Pagination meta={meta} onPageChange={setPage} />

      {users.length === 0 && (
        <div className="text-center py-12">
          <User className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Foydalanuvchilar topilmadi</h3>
          <p className="text-gray-500">Tizimni boshqarish uchun foydalanuvchilar yarating.</p>
        </div>
      )}
    </div>
  );
}

function UserForm({
  initialData,
  roles,
  branches,
  organizations,
  employees,
  canSelectOrganization,
  isEdit,
  onSubmit,
  onCancel,
}: {
  initialData?: any;
  roles: any[];
  branches: any[];
  organizations: any[];
  employees: any[];
  canSelectOrganization: boolean;
  isEdit: boolean;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    email: initialData?.email || '',
    telegram_id: initialData?.telegram_id || '',
    password: '',
    role: initialData?.role || initialData?.roles?.[0]?.name || '',
    org_id: initialData?.org_id || '',
    branch_id: initialData?.branch_id || '',
    scope_level: initialData?.scope_level || 'branch',
    branch_ids: ((initialData?.branches ?? []) as any[]).map((b: any) => String(b.id)),
    employee_id: initialData?.employee?.id ? String(initialData.employee.id) : '',
    is_active: initialData?.is_active ?? true,
  });

  const toggleBranchId = (branchId: string) => {
    setFormData((prev) => ({
      ...prev,
      branch_ids: prev.branch_ids.includes(branchId)
        ? prev.branch_ids.filter((id: string) => id !== branchId)
        : [...prev.branch_ids, branchId],
    }));
  };

  // When editing, include the currently linked employee in the dropdown
  const allAvailable = isEdit && initialData?.employee
    ? [initialData.employee, ...employees.filter((e: any) => e.id !== initialData.employee.id)]
    : employees;

  // When employee is selected, auto-fill name
  const handleEmployeeSelect = (employeeId: string) => {
    const emp = employees.find((e: any) => String(e.id) === employeeId);
    const empBranchId = emp?.branch_id ? String(emp.branch_id) : formData.branch_id;

    setFormData({
      ...formData,
      employee_id: employeeId,
      name: emp?.full_name || formData.name,
      org_id: emp?.org_id ? String(emp.org_id) : formData.org_id,
      branch_id: empBranchId,
      branch_ids:
        empBranchId && !formData.branch_ids.includes(empBranchId)
          ? [...formData.branch_ids, empBranchId]
          : formData.branch_ids,
    });
  };

  const filteredBranches = formData.org_id
    ? branches.filter((b: any) => String(b.org_id) === String(formData.org_id))
    : branches;

  const filteredEmployees = formData.org_id
    ? allAvailable.filter((e: any) => String(e.org_id) === String(formData.org_id))
    : allAvailable;

  return (
    <div className="space-y-4">
      {/* Employee linking */}
      {allAvailable.length > 0 && (
        <div className="p-3 bg-blue-50 rounded-lg">
          <label className="block text-sm font-medium text-blue-800 mb-2">
            Xodimga bog'lash {!isEdit && '(ixtiyoriy)'}
          </label>
          <select
            className="w-full px-4 py-2 border rounded-lg bg-white"
            value={formData.employee_id}
            onChange={(e) => handleEmployeeSelect(e.target.value)}
          >
            <option value="">— Yangi user (xodimsiz) —</option>
            {filteredEmployees.map((emp: any) => (
              <option key={emp.id} value={emp.id}>
                {emp.full_name} ({emp.employee_id}) - {emp.branch?.name || ''}
              </option>
            ))}
          </select>
          <p className="text-xs text-blue-600 mt-1">
            {isEdit ? "Bog'langan xodimni o'zgartirish yoki olib tashlash" : "Xodim tanlasangiz, unga tizimga kirish huquqi beriladi"}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          name="full_name"
          autoComplete="off"
          data-lpignore="true"
          placeholder="To'liq ism"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
        <Input
          type="email"
          name="email"
          autoComplete="new-password"
          data-lpignore="true"
          placeholder="Email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        />
      </div>
      <Input
        name="telegram_id"
        autoComplete="off"
        data-lpignore="true"
        placeholder="Telegram ID yoki @username"
        value={formData.telegram_id}
        onChange={(e) => setFormData({ ...formData, telegram_id: e.target.value })}
      />
      {!isEdit && (
        <Input
          type="password"
          name="password"
          autoComplete="new-password"
          data-lpignore="true"
          placeholder="Parol"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
        />
      )}
      <select
        className="w-full px-4 py-2 border rounded-lg"
        value={formData.role}
        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
      >
        <option value="">Rol tanlang</option>
        {roles.map((role: any) => (
          <option key={role.id || role.name} value={role.name}>
            {role.name}
          </option>
        ))}
      </select>
      {canSelectOrganization && (
        <select
          className="w-full px-4 py-2 border rounded-lg"
          value={formData.org_id}
          onChange={(e) =>
            setFormData({ ...formData, org_id: e.target.value, branch_id: '', branch_ids: [] })
          }
        >
          <option value="">Tashkilot tanlang</option>
          {organizations.map((org: any) => (
            <option key={org.id} value={org.id}>
              {org.name}
            </option>
          ))}
        </select>
      )}
      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700">Kirish doirasi</label>
        <select
          className="w-full px-4 py-2 border rounded-lg"
          value={formData.scope_level}
          onChange={(e) => setFormData({ ...formData, scope_level: e.target.value })}
        >
          <option value="branch">Tanlangan filiallar</option>
          <option value="org">Butun tashkilot (kelajakdagi filiallar ham)</option>
          {canSelectOrganization && <option value="global">Barcha tashkilotlar</option>}
        </select>
      </div>
      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700">
          {formData.scope_level === 'branch' ? 'Asosiy filial' : 'Standart filial (ixtiyoriy)'}
        </label>
        <select
          className="w-full px-4 py-2 border rounded-lg"
          value={formData.branch_id}
          onChange={(e) => {
            const branchId = e.target.value;
            setFormData((prev) => ({
              ...prev,
              branch_id: branchId,
              // The primary branch is always part of the grant.
              branch_ids:
                branchId && !prev.branch_ids.includes(branchId)
                  ? [...prev.branch_ids, branchId]
                  : prev.branch_ids,
            }));
          }}
        >
          <option value="">Filial tanlang</option>
          {filteredBranches.map((branch: any) => (
            <option key={branch.id} value={branch.id}>
              {branch.name}
            </option>
          ))}
        </select>
      </div>
      {formData.scope_level === 'branch' && (
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            Kirish ruxsati bo'lgan filiallar
          </label>
          <div className="max-h-44 space-y-1 overflow-y-auto rounded-lg border px-3 py-2">
            {filteredBranches.length === 0 && (
              <p className="text-sm text-gray-500">Avval tashkilotni tanlang.</p>
            )}
            {filteredBranches.map((branch: any) => (
              <label key={branch.id} className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={formData.branch_ids.includes(String(branch.id))}
                  onChange={() => toggleBranchId(String(branch.id))}
                />
                <span className="text-sm text-gray-700">{branch.name}</span>
              </label>
            ))}
          </div>
          {formData.branch_ids.length === 0 && (
            <p className="text-sm text-red-600">Kamida bitta filial tanlanishi kerak.</p>
          )}
        </div>
      )}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.is_active}
          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
        <span className="text-sm text-gray-700">Faol foydalanuvchi</span>
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          onClick={() =>
            onSubmit({
              ...formData,
              branch_id: formData.branch_id || null,
              branch_ids:
                formData.scope_level === 'branch' ? formData.branch_ids.map(Number) : [],
            })
          }
          disabled={formData.scope_level === 'branch' && formData.branch_ids.length === 0}
          className="flex-1"
        >
          Saqlash
        </Button>
        <Button onClick={onCancel} variant="outline" className="flex-1">
          Bekor qilish
        </Button>
      </div>
    </div>
  );
}

function PasswordForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (data: any) => void;
  onCancel: () => void;
}) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = () => {
    if (password !== confirmPassword) {
      toast.error('Parollar mos kelmadi');
      return;
    }
    if (password.length < 8) {
      toast.error('Parol kamida 8 ta belgidan iborat bo\'lishi kerak');
      return;
    }
    onSubmit({ password, password_confirmation: confirmPassword });
  };

  return (
    <div className="space-y-4">
      <Input
        type="password"
        placeholder="Yangi parol"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <Input
        type="password"
        placeholder="Parolni tasdiqlang"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
      />
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button onClick={handleSubmit} className="flex-1">
          Parolni o'zgartirish
        </Button>
        <Button onClick={onCancel} variant="outline" className="flex-1">
          Bekor qilish
        </Button>
      </div>
    </div>
  );
}


